import { observer } from 'mobx-react-lite';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Button, Text, XStack, YStack } from '@/components/ui';
import { Separator } from '@/components/ui/Separator';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { ReferralCta } from './referral-cta';
import { SUBSCRIPTION_FEATURES } from './subscription.constants';

interface JournalPaywallOverlayProps {
  className?: string;
}

export const JournalPaywallOverlay = observer(
  ({ className }: JournalPaywallOverlayProps) => {
    const { setHash } = useHashRoute();

    const handleUpgrade = () => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'journal_paywall' });
      setHash('settings/Subscription');
    };

    return (
      <div className={cn('absolute inset-0', className)}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-surface pointer-events-none z-10" />
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-6 z-20">
          <div className="bg-surface/95 backdrop-blur-md w-full rounded-2xl border border-border shadow-xl">
            <YStack className="gap-6 p-6">
              <YStack className="gap-1">
                <XStack className="gap-3 items-center justify-center">
                  <AppLogo size={20} />
                  <h2 className="text-xl font-serif text-foreground font-bold">
                    Unlock Full Journal History
                  </h2>
                </XStack>
                <Text
                  variant="subtle"
                  size="sm"
                  className="leading-normal text-foreground-muted text-center"
                >
                  Upgrade to access all your past journal entries.
                </Text>
              </YStack>

              <YStack className="gap-3 bg-surface-hover border border-border-strong rounded-lg p-4">
                {SUBSCRIPTION_FEATURES.slice(0, 4).map((feature, index) => (
                  <XStack key={index} className="gap-3 items-start">
                    <Icons.check className="w-4 h-4 text-success-9" />
                    <Text fontWeight="medium" size="sm">
                      {feature.title}
                    </Text>
                  </XStack>
                ))}
                <YStack className="gap-3 mt-2">
                  <Separator />
                  <Text variant="muted" size="xs">
                    *Subject to fair use policy
                  </Text>
                </YStack>
              </YStack>
              <Button onClick={handleUpgrade} className="w-full" size="default">
                Upgrade
              </Button>
              <ReferralCta source="journal_paywall" className="text-center" />
            </YStack>
          </div>
        </div>
      </div>
    );
  }
);
